const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    display: contents;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .layer{
    position: fixed;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    padding: var(--ctl-space-6);
    z-index: var(--ctl-z-modal);
  }

  :host([open]) .layer{
    display: flex;
  }

  .backdrop{
    position: absolute;
    inset: 0;
    background: var(--ctl-color-backdrop);
  }

  .dialog{
    position: relative;
    width: min(var(--ctl-dialog-max-width, 640px), calc(100vw - var(--ctl-overlay-inset, 48px)));
    max-height: calc(100vh - var(--ctl-overlay-inset, 48px));
    background: var(--ctl-color-surface);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-dialog);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .header{
    display:flex;
    align-items:flex-start;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    padding: var(--ctl-space-4);
    border-bottom: var(--ctl-border-width-thin) solid var(--ctl-color-border);
  }

  .title{
    min-width: 0;
  }

  .title ::slotted(*){
    margin: 0;
  }

  .close{
    all: unset;
    cursor: pointer;
    width: var(--ctl-size-button-sm);
    height: var(--ctl-size-button-sm);
    border-radius: var(--ctl-radius-md);
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: var(--ctl-color-muted);
    transition: background var(--ctl-transition-fast), color var(--ctl-transition-fast);
    flex: 0 0 auto;
  }
  .close:hover{ background: color-mix(in srgb, var(--ctl-color-text) 6%, transparent); color: var(--ctl-color-text); }
  .close:focus-visible{
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  .body{
    padding: var(--ctl-space-4);
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  .footer{
    padding: var(--ctl-space-4);
    border-top: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    display:flex;
    justify-content:flex-end;
    gap: var(--ctl-space-2);
  }
  .footer:empty{ display:none; }

  /* Reduced motion friendly: no animations by default here */
</style>

<div class="layer" part="layer">
  <div class="backdrop" part="backdrop"></div>

  <div
    class="dialog"
    part="dialog"
    role="dialog"
    aria-modal="true"
    aria-labelledby="ctlDialogTitle"
    aria-describedby="ctlDialogBody"
    tabindex="-1"
  >
    <div class="header" part="header">
      <div class="title" id="ctlDialogTitle" part="title">
        <slot name="title"></slot>
      </div>

      <button class="close" id="closeBtn" type="button" aria-label="Close" title="Close" part="close">
        ×
      </button>
    </div>

    <div class="body" id="ctlDialogBody" part="body">
      <slot></slot>
    </div>

    <div class="footer" part="footer">
      <slot name="footer"></slot>
    </div>
  </div>
</div>
`;

function getFocusable(root) {
  const selectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ];
  return Array.from(root.querySelectorAll(selectors.join(",")))
    .filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));
}

export class CtlDialog extends HTMLElement {
  static get observedAttributes() {
    return ["open", "aria-label", "aria-labelledby", "aria-describedby"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._layer = this.shadowRoot.querySelector(".layer");
    this._backdrop = this.shadowRoot.querySelector(".backdrop");
    this._dialog = this.shadowRoot.querySelector(".dialog");
    this._closeBtn = this.shadowRoot.getElementById("closeBtn");

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onBackdropDown = this._onBackdropDown.bind(this);

    this._lastActive = null;
  }

  connectedCallback() {
    // A11y passthrough overrides (optional)
    this._syncAria();

    this._closeBtn.addEventListener("click", () => this.close("close-button"));
    this._backdrop.addEventListener("pointerdown", this._onBackdropDown);
  }

  disconnectedCallback() {
    this._backdrop.removeEventListener("pointerdown", this._onBackdropDown);
    document.removeEventListener("keydown", this._onKeyDown, true);
  }

  attributeChangedCallback() {
    this._syncAria();
    this._syncOpen();
  }

  // --- Public API ---
  show() {
    if (this.hasAttribute("open")) return;
    this._lastActive = document.activeElement;
    this.setAttribute("open", "");
  }

  close(reason = "programmatic") {
    if (!this.hasAttribute("open")) return;
    this._closeReason = reason;
    this.removeAttribute("open");
  }

  toggle() {
    this.hasAttribute("open") ? this.close("toggle") : this.show();
  }

  // --- Internal ---
  _syncAria() {
    // Allow consumers to override labeling if they want
    const label = this.getAttribute("aria-label");
    const labelledby = this.getAttribute("aria-labelledby");
    const describedby = this.getAttribute("aria-describedby");

    if (label) {
      this._dialog.setAttribute("aria-label", label);
      this._dialog.removeAttribute("aria-labelledby");
    } else {
      this._dialog.removeAttribute("aria-label");
      if (labelledby) this._dialog.setAttribute("aria-labelledby", labelledby);
      else this._dialog.setAttribute("aria-labelledby", "ctlDialogTitle");
    }

    if (describedby) this._dialog.setAttribute("aria-describedby", describedby);
    else this._dialog.setAttribute("aria-describedby", "ctlDialogBody");
  }

  _syncOpen() {
    const isOpen = this.hasAttribute("open");

    if (isOpen) {
      // Focus + trap
      document.addEventListener("keydown", this._onKeyDown, true);

      // Focus first focusable, else dialog container
      queueMicrotask(() => {
        const focusables = getFocusable(this._dialog);
        (focusables[0] || this._dialog).focus({ preventScroll: true });
      });

      this.dispatchEvent(
        new CustomEvent("ctl-open", { bubbles: true, composed: true, detail: {} })
      );
    } else {
      document.removeEventListener("keydown", this._onKeyDown, true);

      // Restore focus
      queueMicrotask(() => {
        if (this._lastActive && typeof this._lastActive.focus === "function") {
          this._lastActive.focus({ preventScroll: true });
        }
        this._lastActive = null;
      });

      this.dispatchEvent(
        new CustomEvent("ctl-close", {
          bubbles: true,
          composed: true,
          detail: { reason: this._closeReason || "programmatic" }
        })
      );
      this._closeReason = null;
    }
  }

  _onBackdropDown(e) {
    if (this.hasAttribute("no-backdrop-close")) return;

    // Only close when clicking the backdrop itself
    const path = e.composedPath ? e.composedPath() : [];
    if (path.includes(this._backdrop)) {
      this.close("backdrop");
    }
  }

  _onKeyDown(e) {
    if (!this.hasAttribute("open")) return;

    if (e.key === "Escape" && !this.hasAttribute("no-esc-close")) {
      e.preventDefault();
      e.stopPropagation();
      this.close("escape");
      return;
    }

    if (e.key !== "Tab") return;

    // Focus trap
    const focusables = getFocusable(this._dialog);
    if (!focusables.length) {
      e.preventDefault();
      this._dialog.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = this.shadowRoot.activeElement || document.activeElement;

    if (e.shiftKey) {
      if (active === first || active === this._dialog) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

customElements.define("ctl-dialog", CtlDialog);
